import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useCreatePresentation,
  usePresentation,
  usePublishPresentationVersion,
  useUpdatePresentation,
  useValidatePresentation,
  type CreatePresentationInput,
  type PresentationAudience,
  type ValidationResult,
} from '../api/presentations.js';
import { useFolders } from '../api/folders.js';
import {
  formatBytes,
  inlinePresentationHtml,
  VALIDATOR_LIMITS,
  type InlinerStats,
} from '../lib/inline-presentation-assets.js';
import { UNTRUSTED_FRAME_SANDBOX_WITH_MODALS } from '../lib/untrusted-frame.js';

/**
 * PresentationEditorPage — the third authoring surface's "create / edit"
 * companion. Paste a deck's HTML or drop a .html file; we run the same
 * in-browser inliner the CLI ships (fetch externals → data: URIs, swap
 * Font Awesome <i> tags for inline SVG, strip preconnects), surface the
 * server's validator verdict, render a live preview, and then publish
 * a new version (or create the presentation).
 *
 * Two routes:
 *   /presentations/new         — new presentation, sets v1 on first save
 *   /presentations/:id/edit    — edit metadata + publish a new version
 *
 * Why both paste AND drop:
 *   * Paste: authors who composed the deck in Claude / their editor
 *     just paste the full HTML into the textarea.
 *   * Drop: authors with a deck file from disk drop it on the zone;
 *     the file's bytes go straight into the textarea, then the inliner
 *     button does the heavy lifting (Google Fonts, FA, etc.).
 *
 * The inliner is opt-in (a button, not auto) so authors who already
 * pre-inlined locally don't pay the network round-trips.
 */
export function PresentationEditorPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;
  const existing = usePresentation(isEdit ? (id as string) : null);

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [audience, setAudience] = useState<PresentationAudience>('internal');
  const [folderId, setFolderId] = useState<string | null>(null);
  const [html, setHtml] = useState('');

  // Tooling state
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [serverValidation, setServerValidation] = useState<ValidationResult | null>(null);
  const [inlinerStats, setInlinerStats] = useState<InlinerStats | null>(null);
  const [inlining, setInlining] = useState(false);
  const [inlinerProgress, setInlinerProgress] = useState<string>('');
  const [saveError, setSaveError] = useState<string | null>(null);

  // Folders for the picker. We list both `presentations` (the dedicated
  // type added in migration 034) AND `mixed` so authors with decks
  // already filed in a `mixed` "Presentations" folder (the pre-034
  // shape) keep seeing it. Tenants that want a cleaner taxonomy can
  // create a `presentations`-typed folder and PATCH decks across.
  const presFolders = useFolders('presentations');
  const mixedFolders = useFolders('mixed');
  const folders = {
    data: [...(presFolders.data ?? []), ...(mixedFolders.data ?? [])],
  };

  // Hydrate form from the existing presentation on edit.
  useEffect(() => {
    if (isEdit && existing.data) {
      setName(existing.data.name);
      setDescription(existing.data.description ?? '');
      setAudience(existing.data.audience);
      setFolderId(existing.data.folderId);
      // Initial HTML = current published version. Authors can edit
      // and save → publishes a NEW immutable version (v2, v3…).
      setHtml(existing.data.currentVersion?.html ?? '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, existing.data?.id]);

  // Update the preview blob whenever the user explicitly clicks Preview
  // (NOT on every keystroke — preview thrashing on a 4MB document is
  // expensive). The preview button updates the blob URL below.
  useEffect(() => {
    return () => {
      if (previewUrl) {
        try {
          URL.revokeObjectURL(previewUrl);
        } catch {
          /* noop */
        }
      }
    };
  }, [previewUrl]);

  const create = useCreatePresentation();
  const publishVersion = usePublishPresentationVersion();
  const updateMeta = useUpdatePresentation();
  const validate = useValidatePresentation();

  const inputBytes = useMemo(() => new TextEncoder().encode(html).length, [html]);
  const overSize = inputBytes > VALIDATOR_LIMITS.maxBytes;

  function refreshPreview(): void {
    if (!html.trim()) return;
    if (previewUrl) {
      try {
        URL.revokeObjectURL(previewUrl);
      } catch {
        /* noop */
      }
    }
    const blob = new Blob([html], { type: 'text/html' });
    setPreviewUrl(URL.createObjectURL(blob));
  }

  async function handleInline(): Promise<void> {
    if (!html.trim()) return;
    setInlining(true);
    setInlinerProgress('Starting…');
    setInlinerStats(null);
    try {
      const { html: out, stats } = await inlinePresentationHtml(html, {
        swapFontAwesome: true,
        onProgress: (msg) => setInlinerProgress(msg),
      });
      setHtml(out);
      setInlinerStats(stats);
      setInlinerProgress('');
      // Auto-refresh preview after inlining so authors see the result.
      const blob = new Blob([out], { type: 'text/html' });
      if (previewUrl) {
        try {
          URL.revokeObjectURL(previewUrl);
        } catch {
          /* noop */
        }
      }
      setPreviewUrl(URL.createObjectURL(blob));
    } catch (err) {
      setInlinerProgress(`Failed: ${(err as Error).message}`);
    } finally {
      setInlining(false);
    }
  }

  async function handleValidate(): Promise<void> {
    if (!html.trim()) return;
    try {
      const r = await validate.mutateAsync(html);
      setServerValidation(r);
    } catch (err) {
      setServerValidation({
        ok: false,
        issues: [{ severity: 'error', code: 'validate.failed', message: (err as Error).message }],
        stats: { bytes: inputBytes, approxSlideCount: 0, hasTitle: false },
      });
    }
  }

  async function handleSave(): Promise<void> {
    setSaveError(null);
    try {
      if (!isEdit) {
        const input: CreatePresentationInput = {
          name: name.trim(),
          audience,
          html,
          ...(description.trim() ? { description: description.trim() } : {}),
          ...(folderId ? { folderId } : {}),
        };
        const created = await create.mutateAsync(input);
        navigate(`/presentations/${created.id}`);
      } else {
        // Patch metadata if anything changed.
        const patch: Parameters<typeof updateMeta.mutateAsync>[0]['patch'] = {};
        if (name.trim() !== existing.data?.name) patch.name = name.trim();
        if ((description.trim() || null) !== (existing.data?.description ?? null)) {
          patch.description = description.trim() || null;
        }
        if (audience !== existing.data?.audience) patch.audience = audience;
        if (folderId !== (existing.data?.folderId ?? null)) patch.folderId = folderId;
        if (Object.keys(patch).length > 0) {
          await updateMeta.mutateAsync({ id: id as string, patch });
        }
        // Publish a new version only if HTML actually changed.
        if (html !== existing.data?.currentVersion?.html) {
          await publishVersion.mutateAsync({ id: id as string, html });
        }
        navigate(`/presentations/${id}`);
      }
    } catch (err) {
      setSaveError((err as Error).message);
    }
  }

  const canSave =
    name.trim().length > 0 &&
    html.trim().length >= 200 &&
    !overSize &&
    !create.isPending &&
    !publishVersion.isPending &&
    !updateMeta.isPending;

  return (
    <div className="pres-editor">
      <header className="pres-editor-header">
        <div>
          <h1 className="pres-editor-title">
            {isEdit
              ? `Edit · ${name || existing.data?.name || 'Presentation'}`
              : 'New presentation'}
          </h1>
          <p className="pres-editor-sub">
            {isEdit
              ? 'Edit metadata or publish a new version. Each save creates an immutable version — the viewer flips to the new one automatically.'
              : 'Paste a deck’s HTML or drop a .html file. Run the inliner to fold in external assets, validate, preview, then publish v1.'}
          </p>
        </div>
        <div className="pres-editor-actions">
          <button
            type="button"
            className="btn"
            onClick={() => navigate(isEdit ? `/presentations/${id}` : '/presentations')}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => void handleSave()}
            disabled={!canSave}
            title={!canSave ? 'Fill in name + HTML (≥200 bytes, ≤5 MB)' : undefined}
          >
            {create.isPending || publishVersion.isPending || updateMeta.isPending
              ? 'Saving…'
              : isEdit
                ? 'Save & publish version'
                : 'Create & publish v1'}
          </button>
        </div>
      </header>

      {saveError && (
        <div className="pres-editor-error" role="alert">
          <strong>Save failed:</strong> {saveError}
        </div>
      )}

      <div className="pres-editor-grid">
        {/* Left: form + HTML input */}
        <section className="pres-editor-form">
          <div className="form-row">
            <label className="form-label" htmlFor="pres-name">
              Name
            </label>
            <input
              id="pres-name"
              type="text"
              className="form-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Q3 Operations Review"
              maxLength={200}
            />
          </div>

          <div className="form-row">
            <label className="form-label" htmlFor="pres-desc">
              Description
            </label>
            <textarea
              id="pres-desc"
              className="form-input"
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="One-paragraph summary (optional)"
            />
          </div>

          <div className="form-row form-row--inline">
            <div className="form-col">
              <label className="form-label" htmlFor="pres-audience">
                Audience
              </label>
              <select
                id="pres-audience"
                className="form-input"
                value={audience}
                onChange={(e) => setAudience(e.target.value as PresentationAudience)}
              >
                <option value="internal">internal</option>
                <option value="customer">customer</option>
                <option value="external">external</option>
              </select>
            </div>
            <div className="form-col">
              <label className="form-label" htmlFor="pres-folder">
                Folder
              </label>
              <select
                id="pres-folder"
                className="form-input"
                value={folderId ?? ''}
                onChange={(e) => setFolderId(e.target.value || null)}
              >
                <option value="">— root —</option>
                {folders.data?.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <HtmlInputZone html={html} onChange={setHtml} />

          <div className="pres-editor-tools">
            <button
              type="button"
              className="btn btn--sm"
              onClick={() => void handleInline()}
              disabled={!html.trim() || inlining}
            >
              {inlining ? 'Inlining…' : 'Inline external assets'}
            </button>
            <button
              type="button"
              className="btn btn--sm"
              onClick={() => void handleValidate()}
              disabled={!html.trim() || validate.isPending}
            >
              {validate.isPending ? 'Validating…' : 'Validate'}
            </button>
            <button
              type="button"
              className="btn btn--sm"
              onClick={refreshPreview}
              disabled={!html.trim()}
            >
              Refresh preview
            </button>
            <span className="pres-editor-meta">
              {formatBytes(inputBytes)} / {formatBytes(VALIDATOR_LIMITS.maxBytes)}
              {overSize && <span className="pres-editor-meta-err"> · over limit</span>}
            </span>
          </div>

          {(inlinerProgress || inlinerStats) && (
            <InlinerStatsPanel progress={inlinerProgress} stats={inlinerStats} />
          )}
          {serverValidation && <ValidationPanel result={serverValidation} />}
        </section>

        {/* Right: live preview */}
        <section className="pres-editor-preview">
          <div className="pres-editor-preview-head">
            <span className="pres-editor-preview-title">Preview</span>
            <span className="pres-editor-preview-hint">
              Click <em>Refresh preview</em> after edits. (We don’t auto-refresh — large decks would
              thrash.)
            </span>
          </div>
          {previewUrl ? (
            <iframe
              title="Preview"
              src={previewUrl}
              className="pres-editor-iframe"
              sandbox={UNTRUSTED_FRAME_SANDBOX_WITH_MODALS}
            />
          ) : (
            <div className="pres-editor-preview-empty">
              No preview yet. Paste / drop HTML and click <strong>Refresh preview</strong>.
            </div>
          )}
        </section>
      </div>

      <EditorStyles />
    </div>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────

function HtmlInputZone({
  html,
  onChange,
}: {
  html: string;
  onChange: (next: string) => void;
}): JSX.Element {
  const [dragOver, setDragOver] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  function handleDragOver(e: React.DragEvent): void {
    e.preventDefault();
    setDragOver(true);
  }
  function handleDragLeave(): void {
    setDragOver(false);
  }
  function handleDrop(e: React.DragEvent): void {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (!file) return;
    if (!/\.html?$/i.test(file.name) && file.type !== 'text/html') {
      // Allow anyway — many decks ship without an extension — but warn via title.
      // No alert, just trust the user.
    }
    const reader = new FileReader();
    reader.onload = () => {
      const text = typeof reader.result === 'string' ? reader.result : '';
      onChange(text);
    };
    reader.readAsText(file);
  }

  return (
    <div className="form-row form-row--grow">
      <label className="form-label" htmlFor="pres-html">
        Deck HTML
        <span className="form-label-hint"> — paste, or drop a .html file</span>
      </label>
      <div
        className={'pres-html-zone' + (dragOver ? ' pres-html-zone--drag' : '')}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <textarea
          ref={textareaRef}
          id="pres-html"
          className="pres-html-input"
          value={html}
          onChange={(e) => onChange(e.target.value)}
          placeholder={'<!DOCTYPE html>\n<html>…\n\nOr drop a .html file anywhere in this box.'}
          spellCheck={false}
        />
        {dragOver && (
          <div className="pres-html-zone-overlay" aria-hidden="true">
            Drop to load
          </div>
        )}
      </div>
    </div>
  );
}

function InlinerStatsPanel({
  progress,
  stats,
}: {
  progress: string;
  stats: InlinerStats | null;
}): JSX.Element {
  return (
    <div className="pres-editor-panel">
      <div className="pres-editor-panel-title">Inliner</div>
      {progress && <div className="pres-editor-panel-row">{progress}</div>}
      {stats && (
        <>
          <div className="pres-editor-panel-row">
            Inlined: <strong>{stats.inlinedAssets}</strong> asset(s),{' '}
            <strong>{stats.inlinedCss}</strong> stylesheet(s), <strong>{stats.inlinedJs}</strong>{' '}
            script(s).
          </div>
          {stats.faIconsSwapped > 0 && (
            <div className="pres-editor-panel-row">
              Font Awesome icons swapped for inline SVG: <strong>{stats.faIconsSwapped}</strong>.
            </div>
          )}
          {stats.strippedLinks > 0 && (
            <div className="pres-editor-panel-row">
              Stripped <strong>{stats.strippedLinks}</strong> non-stylesheet &lt;link&gt; tag(s).
            </div>
          )}
          <div className="pres-editor-panel-row">
            Size: {formatBytes(stats.inputBytes)} → {formatBytes(stats.outputBytes)} (limit{' '}
            {formatBytes(VALIDATOR_LIMITS.maxBytes)})
            {stats.overSize && <span className="pres-editor-meta-err"> · over limit</span>}
          </div>
          {stats.forbiddenTags.length > 0 && (
            <div className="pres-editor-panel-row pres-editor-panel-row--warn">
              Forbidden tags present — must be removed before save:{' '}
              {stats.forbiddenTags.map((t) => `<${t.tag}>×${t.count}`).join(', ')}
            </div>
          )}
          {stats.errors.length > 0 && (
            <details className="pres-editor-panel-details">
              <summary>{stats.errors.length} error(s)</summary>
              <ul>
                {stats.errors.map((e, i) => (
                  <li key={i}>{e}</li>
                ))}
              </ul>
            </details>
          )}
        </>
      )}
    </div>
  );
}

function ValidationPanel({ result }: { result: ValidationResult }): JSX.Element {
  return (
    <div
      className={
        'pres-editor-panel' + (result.ok ? ' pres-editor-panel--ok' : ' pres-editor-panel--err')
      }
    >
      <div className="pres-editor-panel-title">
        Validation: {result.ok ? 'passes' : 'FAILS'} · {formatBytes(result.stats.bytes)} · ~
        {result.stats.approxSlideCount} slide(s)
      </div>
      {result.issues.length === 0 && (
        <div className="pres-editor-panel-row">No issues — clear to save.</div>
      )}
      {result.issues.map((iss, i) => (
        <div
          key={i}
          className={
            'pres-editor-panel-row pres-editor-panel-row--' +
            (iss.severity === 'error' ? 'warn' : 'note')
          }
        >
          <strong>{iss.severity.toUpperCase()}</strong> · <code>{iss.code}</code> · {iss.message}
        </div>
      ))}
    </div>
  );
}

function EditorStyles(): JSX.Element {
  return (
    <style>{`
      .pres-editor { display: flex; flex-direction: column; gap: var(--space-3); padding: var(--space-3); height: 100%; }
      .pres-editor-header { display: flex; justify-content: space-between; align-items: flex-start; gap: var(--space-3); }
      .pres-editor-title { margin: 0; font-size: var(--font-size-lg); }
      .pres-editor-sub { margin: var(--space-1) 0 0; color: var(--text-dim); font-size: var(--font-size-sm); max-width: 60ch; }
      .pres-editor-actions { display: flex; gap: var(--space-2); flex-shrink: 0; }
      .pres-editor-error {
        padding: var(--space-2) var(--space-3); border-radius: var(--radius-md);
        background: color-mix(in srgb, var(--err) 12%, transparent); color: var(--err);
        font-size: var(--font-size-sm);
      }
      .pres-editor-grid {
        display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1.1fr);
        gap: var(--space-3); flex: 1 1 auto; min-height: 0;
      }
      .pres-editor-form { display: flex; flex-direction: column; gap: var(--space-2); min-height: 0; }
      .form-row { display: flex; flex-direction: column; gap: 4px; }
      .form-row--inline { flex-direction: row; gap: var(--space-2); }
      .form-row--inline .form-col { flex: 1; display: flex; flex-direction: column; gap: 4px; }
      .form-row--grow { flex: 1 1 auto; min-height: 200px; }
      .form-label { font-size: var(--font-size-xs); color: var(--text-dim); text-transform: uppercase; letter-spacing: var(--letter-wide); }
      .form-label-hint { text-transform: none; letter-spacing: normal; color: var(--text-mute); }
      .form-input {
        padding: var(--space-2); background: var(--surface-2); color: var(--text);
        border: 1px solid var(--border); border-radius: var(--radius-md); font-size: var(--font-size-sm);
      }
      .pres-html-zone { position: relative; flex: 1 1 auto; display: flex; min-height: 240px; }
      .pres-html-zone--drag { outline: 2px dashed var(--accent); outline-offset: -2px; }
      .pres-html-input {
        flex: 1 1 auto; padding: var(--space-2); background: var(--surface-2); color: var(--text);
        border: 1px solid var(--border); border-radius: var(--radius-md);
        font-family: var(--font-mono); font-size: 12px; line-height: 1.5; resize: none;
      }
      .pres-html-zone-overlay {
        position: absolute; inset: 0; display: flex; align-items: center; justify-content: center;
        background: color-mix(in srgb, var(--accent) 18%, transparent); border-radius: var(--radius-md);
        color: var(--text); font-weight: 600; pointer-events: none;
      }
      .pres-editor-tools { display: flex; align-items: center; gap: var(--space-2); flex-wrap: wrap; }
      .pres-editor-meta { margin-left: auto; color: var(--text-dim); font-size: var(--font-size-xs); }
      .pres-editor-meta-err { color: var(--err); font-weight: 600; }
      .pres-editor-panel {
        background: var(--surface-2); border: 1px solid var(--border); border-radius: var(--radius-md);
        padding: var(--space-2); font-size: var(--font-size-sm); display: flex; flex-direction: column; gap: 4px;
      }
      .pres-editor-panel--ok { border-color: color-mix(in srgb, var(--ok) 40%, var(--border)); }
      .pres-editor-panel--err { border-color: color-mix(in srgb, var(--err) 40%, var(--border)); }
      .pres-editor-panel-title { font-weight: 600; font-size: var(--font-size-xs); text-transform: uppercase; letter-spacing: var(--letter-wide); color: var(--text-dim); }
      .pres-editor-panel-row { color: var(--text); }
      .pres-editor-panel-row--warn { color: var(--err); }
      .pres-editor-panel-row--note { color: var(--text-dim); }
      .pres-editor-panel-details summary { cursor: pointer; color: var(--text-dim); }
      .pres-editor-panel-details ul { margin: 4px 0 0; padding-left: var(--space-3); }
      .pres-editor-preview { display: flex; flex-direction: column; min-height: 0; background: var(--surface-2); border: 1px solid var(--border); border-radius: var(--radius-md); overflow: hidden; }
      .pres-editor-preview-head { display: flex; justify-content: space-between; align-items: baseline; padding: var(--space-2) var(--space-3); border-bottom: 1px solid var(--border); }
      .pres-editor-preview-title { font-weight: 600; font-size: var(--font-size-sm); }
      .pres-editor-preview-hint { font-size: var(--font-size-xs); color: var(--text-dim); }
      .pres-editor-iframe { flex: 1 1 auto; width: 100%; border: 0; background: white; }
      .pres-editor-preview-empty { flex: 1 1 auto; display: flex; align-items: center; justify-content: center; color: var(--text-dim); font-size: var(--font-size-sm); padding: var(--space-3); text-align: center; }
    `}</style>
  );
}
